import React, { useState } from 'react';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';
import { tipiEnti } from '../mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nomeEnte: '',
    tipoEnte: '',
    referente: '',
    email: '',
    telefono: '',
    messaggio: '',
    privacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione
    if (!formData.privacy) {
      toast.error('Devi accettare la Privacy Policy per continuare');
      return;
    }

    setIsSubmitting(true);

    try {
      // Chiamata API reale
      const response = await axios.post(`${BACKEND_URL}/api/contacts`, {
        nome_ente: formData.nomeEnte,
        tipo_ente: formData.tipoEnte,
        referente: formData.referente,
        email: formData.email,
        telefono: formData.telefono,
        messaggio: formData.messaggio,
        privacy: formData.privacy
      });
      
      if (response.data.status === 'success') {
        setIsSuccess(true);
        toast.success(response.data.message);
        
        // Reset form dopo 3 secondi
        setTimeout(() => {
          setFormData({
            nomeEnte: '',
            tipoEnte: '',
            referente: '',
            email: '',
            telefono: '',
            messaggio: '',
            privacy: false
          });
          setIsSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Errore invio form:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contatti" className="py-20 bg-gradient-to-b from-[#2B7A9F] to-[#17536B] relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#3AAFA9]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5FB76E]/10 rounded-full blur-3xl"></div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-md text-white rounded-full text-sm font-semibold mb-4 border border-white/20">
            Diagnosi di Sostenibilità Assicurativa
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Proteggi il tuo ente,{' '}
            <span className="text-[#F9D424]">inizia oggi</span>
          </h2>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Compila il form per richiedere una Diagnosi di Sostenibilità Assicurativa gratuita. 
            Ti ricontatteremo entro 24 ore.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-8 sm:p-12 shadow-2xl animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {isSuccess ? (
            <div className="text-center py-12 animate-fade-in-up">
              <div className="w-20 h-20 bg-[#5FB76E]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-[#5FB76E]" size={48} />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Richiesta Inviata con Successo!
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Ti contatteremo entro 24 ore per fissare un appuntamento.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nome Ente */}
              <div>
                <label htmlFor="nomeEnte" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome dell'Ente *
                </label>
                <input
                  type="text"
                  id="nomeEnte"
                  name="nomeEnte"
                  value={formData.nomeEnte}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7A9F] focus:border-transparent transition-all duration-300 outline-none"
                  placeholder="Es. Cooperativa Sociale Il Sole"
                />
              </div>

              {/* Tipo Ente */}
              <div>
                <label htmlFor="tipoEnte" className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipologia Ente *
                </label>
                <select
                  id="tipoEnte"
                  name="tipoEnte"
                  value={formData.tipoEnte}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7A9F] focus:border-transparent transition-all duration-300 outline-none"
                >
                  <option value="">Seleziona una tipologia</option>
                  {tipiEnti.map((tipo, index) => (
                    <option key={index} value={tipo}>
                      {tipo}
                    </option>
                  ))}
                </select>
              </div>

              {/* Referente */}
              <div>
                <label htmlFor="referente" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome Referente *
                </label>
                <input
                  type="text"
                  id="referente"
                  name="referente"
                  value={formData.referente}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7A9F] focus:border-transparent transition-all duration-300 outline-none"
                  placeholder="Mario Rossi"
                />
              </div>

              {/* Email e Telefono */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7A9F] focus:border-transparent transition-all duration-300 outline-none"
                    placeholder="info@esempio.it"
                  />
                </div>
                <div>
                  <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7A9F] focus:border-transparent transition-all duration-300 outline-none"
                    placeholder="+39 123 456 7890"
                  />
                </div>
              </div>

              {/* Messaggio */}
              <div>
                <label htmlFor="messaggio" className="block text-sm font-semibold text-gray-700 mb-2">
                  Messaggio (opzionale)
                </label>
                <textarea
                  id="messaggio"
                  name="messaggio"
                  value={formData.messaggio}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B7A9F] focus:border-transparent transition-all duration-300 outline-none resize-none"
                  placeholder="Descrivi brevemente le tue esigenze o domande..."
                ></textarea>
              </div>

              {/* Privacy Checkbox */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleChange}
                  required
                  className="mt-1 w-5 h-5 text-[#2B7A9F] border-gray-300 rounded focus:ring-[#2B7A9F] cursor-pointer"
                />
                <label htmlFor="privacy" className="text-sm text-gray-600 cursor-pointer">
                  Dichiaro di aver preso visione dell'<a href="/privacy-policy" className="text-[#2B7A9F] underline" target="_blank" rel="noopener noreferrer">Informativa Privacy</a> e acconsento al trattamento dei miei dati personali ai sensi del GDPR (Regolamento UE 2016/679). 
                  I dati saranno utilizzati esclusivamente per rispondere alla richiesta di contatto. *
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#E74C3C] to-[#E67E22] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    <span>Invio in corso...</span>
                  </>
                ) : (
                  <>
                    <span>Richiedi Diagnosi Gratuita</span>
                    <Send size={20} />
                  </>
                )}
              </button>

              {/* Privacy Note */}
              <p className="text-xs text-gray-500 text-center">
                * Campi obbligatori. Rispondiamo entro 24 ore lavorative.
              </p>
            </form>
          )}
        </div>

        {/* Contact Info */}
        <div className="mt-12 text-center text-white animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <p className="text-lg mb-4">Preferisci parlare direttamente con noi?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+390785747193"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-3 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              <span className="font-semibold text-sm">📞 0785 747193</span>
            </a>
            <a
              href="tel:+393737288673"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-3 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              <span className="font-semibold text-sm">📱 373 7288673</span>
            </a>
            <a
              href="mailto:agenzia@assietica.it"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-3 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              <span className="font-semibold text-sm">✉️ agenzia@assietica.it</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
